import { DIMENSION_KINDS, dimensionDefinition } from '../dimensions';
import { definitionOf, ENTITIES } from '../entities';
import type { Shape } from '../geom';
import {
  cleanStyle,
  hasStyle,
  DEFAULT_DISPLAY,
  emptyDocument,
  type DrawingDocument,
  type DimensionKind,
  type DimensionNode,
  type EntityNode,
  type EntityType,
  type Layer,
  type NodeId,
  type SceneNode,
  type Style,
  type LengthDisplay,
  type ViewNode,
} from '../model';
import { CALLS, VIEW_CODE } from './api';
import { withBaseLayer, type ScriptLayers } from './layers';
import type { ScriptOutput } from './runtime';

const isEntityType = (type: string): type is EntityType => Object.hasOwn(ENTITIES, type);

/** A drawn shape, a dimension's kind and its recorded numbers, or a view's margin. */
type Decoded =
  | Shape
  | { readonly type: 'dimension'; readonly kind: DimensionKind; readonly numbers: number[] }
  | { readonly type: 'view'; readonly margin: number };

/** How many numbers follow a record's code, and how to read them; dimensions follow shapes. */
function callOf(code: number): { arity: number; decode(numbers: number[]): Decoded } | undefined {
  const shape = CALLS[code];
  if (shape) return shape;
  if (code === VIEW_CODE)
    return { arity: 1, decode: ([margin]) => ({ type: 'view', margin: margin! }) };
  const kind = DIMENSION_KINDS[code - CALLS.length];
  if (kind === undefined) return undefined;
  return {
    arity: dimensionDefinition(kind).arity,
    decode: (numbers) => ({ type: 'dimension', kind, numbers }),
  };
}

/** Records come out of the sandbox, so they are checked before they are trusted. */
function decodeAll(records: ArrayLike<number>): Decoded[] {
  const decoded: Decoded[] = [];
  let at = 0;
  while (at < records.length) {
    const call = callOf(records[at]!);
    const end = at + 1 + (call?.arity ?? 0);
    let valid = call !== undefined && end <= records.length;
    for (let i = at + 1; valid && i < end; i += 1) valid = Number.isFinite(records[i]);
    if (!valid || !call)
      throw new Error(`Script output ${decoded.length + 1} is not a drawing call`);
    decoded.push(call.decode(Array.prototype.slice.call(records, at + 1, end)));
    at = end;
  }
  return decoded;
}

/**
 * The document's layers, in the order the script ran with; layer `0` is `layer-1`, matching
 * `DEFAULT_LAYER`, so a drawing without layers keeps its ids.
 */
function documentLayers(layers: ScriptLayers): Layer[] {
  return withBaseLayer(layers).map(({ name, style, visible, locked }, index) => {
    const layer: Layer = { id: `layer-${index + 1}`, name, visible, locked };
    return hasStyle(style) ? { ...layer, style } : layer;
  });
}

function decodeStyled(styled: ScriptOutput['styled']): Map<number, Style> {
  return new Map(styled.map(({ at, style }) => [at, cleanStyle(style)]));
}

/** Each text a shape carries, by record index; anything but a string is ignored, as names are. */
function decodeLabeled(labeled: ScriptOutput['labeled']): Map<number, string> {
  const labels = new Map<number, string>();
  for (const { at, text } of labeled ?? []) if (typeof text === 'string') labels.set(at, text);
  return labels;
}

function decodeLayered(layered: readonly unknown[], count: number): Map<number, number> {
  const layerOf = new Map<number, number>();
  for (let at = 0; at < layered.length; at += 2) {
    const index = layered[at] as number;
    const layer = layered[at + 1] as number;
    if (!Number.isInteger(layer) || layer < 0 || layer >= count)
      throw new Error(`Script output ${index + 1} has no layer`);
    layerOf.set(index, layer);
  }
  return layerOf;
}

/** Each view's members, by record index, from pairs of a member's index and its view's. */
function decodeViewed(
  viewed: readonly unknown[],
  decoded: readonly Decoded[],
): Map<number, number[]> {
  const members = new Map<number, number[]>();
  for (let at = 0; at < viewed.length; at += 2) {
    const member = viewed[at] as number;
    const view = viewed[at + 1] as number;
    const kind = Number.isInteger(member) ? decoded[member]?.type : undefined;
    if (kind === undefined || kind === 'view' || decoded[view]?.type !== 'view')
      throw new Error(`Script output ${member + 1} has no view`);
    const list = members.get(view);
    if (list) list.push(member);
    else members.set(view, [member]);
  }
  return members;
}

/** Each field path split once, since a large script writes the same few paths thousands of times. */
const splitPaths = new Map<string, readonly [string, string | null]>();
function splitPath(path: string): readonly [string, string | null] {
  let split = splitPaths.get(path);
  if (!split) {
    const dot = path.indexOf('.');
    split = dot < 0 ? [path, null] : [path.slice(0, dot), path.slice(dot + 1)];
    splitPaths.set(path, split);
  }
  return split;
}

/**
 * Turns a script's records into a document of plain-number entities and dimensions,
 * numbered per kind in the order the script drew them, so the same script always yields
 * the same ids.
 */
export function buildDocument(
  output: ScriptOutput,
  name: string,
  scriptLayers: ScriptLayers = [],
  display: LengthDisplay = DEFAULT_DISPLAY,
): DrawingDocument {
  const nodes: Record<NodeId, SceneNode> = {};
  const rootIds: NodeId[] = [];
  const counts = new Map<EntityType | 'dimension' | 'view', number>();
  const layers = documentLayers(scriptLayers);
  const layerOf = decodeLayered(output.layered, layers.length);
  const styleOf = decodeStyled(output.styled);
  const labelOf = decodeLabeled(output.labeled);

  const decoded = decodeAll(output.records);
  const viewMembers = decodeViewed(output.viewed ?? [], decoded);
  const ids: NodeId[] = [];
  decoded.forEach((shape, index) => {
    if (shape.type !== 'dimension' && shape.type !== 'view' && !isEntityType(shape.type))
      throw new Error(`Script output ${index + 1} has no entity kind`);
    const count = (counts.get(shape.type) ?? 0) + 1;
    counts.set(shape.type, count);
    const id = `${shape.type}-${count}`;
    ids.push(id);
    const layer = layers[layerOf.get(index) ?? 0]!;
    // Built in place, then never changed, which is far cheaper than a spread per field.
    const node: Record<string, unknown> = {
      id,
      // A .named() name is only text to show, so anything but a string is ignored.
      name: typeof output.names[index] === 'string' ? output.names[index]! : id,
      parentId: null,
      visible: true,
      locked: false,
      layerId: layer.id,
      type: shape.type,
    };
    const style = styleOf.get(index);
    if (style) node.style = style;
    if (shape.type === 'view') {
      if (!(shape.margin >= 0)) throw new Error(`Script output ${index + 1} has a negative margin`);
      node.margin = shape.margin;
      // Pieces made after the view join it too, so members are filled in once all have ids.
      node.memberIds = [];
      nodes[id] = node as unknown as ViewNode;
      rootIds.push(id);
      return;
    }
    if (shape.type === 'dimension') {
      Object.assign(
        node,
        dimensionDefinition(shape.kind).fromRecord(shape.numbers, shape.kind, labelOf.get(index)),
      );
      nodes[id] = node as unknown as DimensionNode;
      rootIds.push(id);
      return;
    }
    const values = definitionOf(shape.type).fromShape(shape);
    for (const path in values) {
      const [head, tail] = splitPath(path);
      if (tail === null) node[head] = values[path];
      else ((node[head] ??= {}) as Record<string, number>)[tail] = values[path]!;
    }
    nodes[id] = node as unknown as EntityNode;
    rootIds.push(id);
  });

  for (const [view, members] of viewMembers) {
    const node = nodes[ids[view]!] as ViewNode;
    nodes[node.id] = { ...node, memberIds: members.map((member) => ids[member]!) };
  }

  return { ...emptyDocument(name), nodes, rootIds, layers, display };
}
